/**
 * Файлы и папки. Модуль для работы с API.
 * @module files
 */

import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получает структуру папок с применением фильтров.
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные папок.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getFolders = async (params, isAdmin = false) => {
  delete params.search;

  const cleanedFilters = cleanParams(params);
  const urlSearch = new URLSearchParams();

  Object.entries(cleanedFilters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => urlSearch.append(`${key}[]`, v));
    } else {
      urlSearch.append(key, value);
    }
  });

  const queryString = urlSearch.toString();

  try {
    const response = await fetch(
      `/api/folders${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    if (!data.result) return [];

    const personalFolder = data.result.find((folder) => folder?.is_personal);
    const otherFolders = data.result.filter((folder) => !folder.is_personal);
    return personalFolder ? [personalFolder, ...otherFolders] : otherFolders;
  } catch (err) {
    throw err;
  }
};

/**
 * Для селектов: Получает список папок с применением фильтров без пагинации.
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные папок.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getFoldersList = async (params, isAdmin = false) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (key !== "units_uuids") {
      searchParams.append(key, value);
    }
  });

  if (isAdmin) {
    if (params?.units_uuids?.length) {
      searchParams.append("unit_uuid", params.units_uuids);
    }
  } else {
    if (params?.units_uuids?.length) {
      params.units_uuids.forEach((uuid) => {
        searchParams.append("units_uuids[]", uuid);
      });
    }
  }

  searchParams.append("page", 1);
  searchParams.append("perpage", maxPerPage);

  const queryString = searchParams.toString();

  const response = await fetch(
    `/api/folderslist${isAdmin ? "/admin" : ""}?${queryString}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(
      `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
    );
  }

  return data.folders;
};

/**
 * Получает данные папки по UUID (название, РГ, описание).
 * @param {string} uuid - UUID папки.
 * @returns {Promise<Object>} - Данные папки.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getFolder = async (uuid, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/folder${isAdmin ? "/admin" : ""}?uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.folder;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новую папку.
 * @param {Object} item - Данные папки.
 * @returns {Promise<Object>} - Созданная папка.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createFolder = async (item) => {
  try {
    const response = await fetch("/api/folder", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.folder;
  } catch (err) {
    throw err;
  }
};

/**
 * Редактирование существующей папки.
 * @param {Object} item - Данные папки.
 * @returns {Promise<Object>} - Обновленная папка.
 * @throws {Error} - Ошибка при обновлении данных.
 */
export const updateFolder = async (item) => {
  try {
    const response = await fetch("/api/folder", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.folder;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление папки.
 * @param {Object} item - Данные папки.
 * @returns {Promise<Object>} - Удаленная папка.
 * @throws {Error} - Ошибка при удалении данных.
 */
export const deleteFolder = async (item) => {
  try {
    const response = await fetch("/api/folder", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.folder;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает данные файла по UUID.
 * @param {string} uuid - UUID файла.
 * @returns {Promise<Object>} - Данные файла.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getFile = async (uuid, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/file${isAdmin ? "/admin" : ""}?uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.file;
  } catch (err) {
    throw err;
  }
};

/**
 * Создать файл и положить его в папку.
 * @param {Object} item - Данные файла.
 * @returns {Promise<Object>} - Созданный файл.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createFile = async (item) => {
  try {
    const response = await fetch("/api/file", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновить файл.
 * @param {Object} item - Данные файла.
 * @returns {Promise<Object>} - Обновленный файл.
 * @throws {Error} - Ошибка при обновлении данных.
 */
export const updateFile = async (item) => {
  try {
    const response = await fetch("/api/file", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удалить файл.
 * @param {Object} item - Данные файла.
 * @returns {Promise<Object>} - Удаленный файл.
 * @throws {Error} - Ошибка при удалении данных.
 */
export const deleteFile = async (item) => {
  try {
    const response = await fetch("/api/file", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

export const deleteFiles = async (items) => {
  try {
    const response = await fetch("/api/files/delete-batch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ file_uuids: items }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.result;
  } catch (err) {
    throw err;
  }
};

/**
 * Создать ярлык и положить его в папку.
 * @param {Object} item - Данные ярлыка.
 * @returns {Promise<Object>} - Созданный ярлык.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createFileLink = async (item) => {
  try {
    const response = await fetch("/api/files/shortcut", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает номер страницы для пагинации по UUID.
 * @param {Object}  - params - {uuid, sort, perpage }
 * @returns {Promise<Object>} - Данные
 * @throws {Error} - Ошибка при получении данных.
 */
export const getFilePage = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);
  try {
    const response = await fetch(
      `/api/foldercontents/resolve-position${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить содержимое папки по uuid
 * @param {Object} params - {uuid, sort, search, unit_uuid, author_uuid, page, perpage } Если uuid нет, возвращает содержимое верхнего уровня
 * @returns {Promise<Object>} - Содержимое папки. Разделение элементов по признаку "element_type": "folder" или "file"
 * @throws {Error} - Ошибка при получении данных.
 */
export const getFolderContent = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/foldercontents${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Глобальный поиск по файлам и папкам
 * @param {Object} params - {search, page, perpage, sort, unit_uuid, author_uuid}  Параметры поиска
 * @returns {Promise<Object>} - Результаты поиска
 * @throws {Error} - Ошибка при получении данных.
 */
export const globalSearchFiles = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/folders/search${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};
